import { useCallback, useMemo, useRef, useState } from "react";
import { SearchOutlined, FilterFilled } from "@ant-design/icons";
import { ColumnSearchDropdown, ColumnSelectDropdown } from "./ColumnFilterDropdowns";

const ACTIVE_ICON_COLOR = "#5D5DF6";

const isEmptyValue = (value) => value === undefined || value === null || value === "";

const STORAGE_PREFIX = "sc-table-filters:";

const readPersisted = (persistKey) => {
    if (!persistKey) return null;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_PREFIX + persistKey);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
};

const writePersisted = (persistKey, filters) => {
    if (!persistKey) return;
    try {
        if (Object.keys(filters).length === 0) {
            window.sessionStorage.removeItem(STORAGE_PREFIX + persistKey);
        } else {
            window.sessionStorage.setItem(STORAGE_PREFIX + persistKey, JSON.stringify(filters));
        }
    } catch {
        // Storage can be unavailable (private mode); filters just won't be remembered.
    }
};

/**
 * One filter state per table: the toolbar search box, every column search dropdown and the
 * "Reset filters" button all read and write the same `filters` object, so resetting it clears
 * everything at once.
 *
 * Works uncontrolled (internal state) or controlled via `value` + `onChange` when the filters
 * live elsewhere (e.g. a context shared with the mobile list).
 *
 * `persistKey` (uncontrolled only) remembers the filters for the browser tab session, so opening
 * a record and coming back keeps the list filtered the way the user left it.
 */
export default function useTableFilters({ value, initialFilters = {}, onChange, persistKey } = {}) {
    const [innerFilters, setInnerFilters] = useState(() => readPersisted(persistKey) ?? initialFilters);
    const filters = value ?? innerFilters;

    // Keep the latest values in refs so the helpers below stay referentially stable.
    const filtersRef = useRef(filters);
    filtersRef.current = filters;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const isControlled = value !== undefined;

    const commit = useCallback(
        (next) => {
            filtersRef.current = next;
            if (!isControlled) {
                setInnerFilters(next);
                writePersisted(persistKey, next);
            }
            onChangeRef.current?.(next);
        },
        [isControlled, persistKey],
    );

    const setFilter = useCallback(
        (key, nextValue) => {
            const { current } = filtersRef;
            if (isEmptyValue(nextValue) && !(key in current)) return;
            if (current[key] === nextValue) return;

            const next = { ...current };
            if (isEmptyValue(nextValue)) {
                delete next[key];
            } else {
                next[key] = nextValue;
            }
            commit(next);
        },
        [commit],
    );

    // Swap one key for another while keeping its value (used by the "search by" field picker).
    const renameFilter = useCallback(
        (fromKey, toKey) => {
            const { current } = filtersRef;
            if (fromKey === toKey || isEmptyValue(current[fromKey])) return;
            const next = { ...current, [toKey]: current[fromKey] };
            delete next[fromKey];
            commit(next);
        },
        [commit],
    );

    const clearFilter = useCallback((key) => setFilter(key, undefined), [setFilter]);

    const resetFilters = useCallback(() => {
        if (Object.keys(filtersRef.current).length === 0) return;
        commit({});
    }, [commit]);

    const getColumnSearchProps = useCallback(
        (key, { placeholder } = {}) => ({
            filterKey: key,
            filteredValue: isEmptyValue(filters[key]) ? null : [filters[key]],
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, visible }) => (
                <ColumnSearchDropdown
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    visible={visible}
                    placeholder={placeholder}
                    onApply={(text) => {
                        setFilter(key, text);
                        confirm();
                    }}
                    onClear={() => {
                        clearFilters?.();
                        clearFilter(key);
                        confirm();
                    }}
                />
            ),
            filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? ACTIVE_ICON_COLOR : undefined }} />,
        }),
        [filters, setFilter, clearFilter],
    );

    const getColumnSelectProps = useCallback(
        (key, options) => ({
            filterKey: key,
            filterOptions: options,
            filteredValue: isEmptyValue(filters[key]) ? null : [filters[key]],
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <ColumnSelectDropdown
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    options={options}
                    onApply={(selected) => {
                        setFilter(key, selected);
                        confirm();
                    }}
                    onClear={() => {
                        clearFilters?.();
                        clearFilter(key);
                        confirm();
                    }}
                />
            ),
            filterIcon: (filtered) => <FilterFilled style={{ color: filtered ? ACTIVE_ICON_COLOR : undefined }} />,
        }),
        [filters, setFilter, clearFilter],
    );

    const activeCount = Object.keys(filters).length;

    return useMemo(
        () => ({
            filters,
            activeCount,
            hasActiveFilters: activeCount > 0,
            setFilter,
            renameFilter,
            clearFilter,
            resetFilters,
            getColumnSearchProps,
            getColumnSelectProps,
        }),
        [
            filters,
            activeCount,
            setFilter,
            renameFilter,
            clearFilter,
            resetFilters,
            getColumnSearchProps,
            getColumnSelectProps,
        ],
    );
}
